import {FiltersBar} from '@components/ui/filters-bar/FiltersBar.tsx'
import {TableInputSearch} from '@components/commons/table-input-search/TableInputSearch.tsx'
import {
    columnVisibilitySearchParamsToPayload,
    columnVisibilityToSearchParams,
    debounce,
    filtersParamToPayload,
    getIsRowSelected,
    sortParamToPayload
} from '@utilities/helpers.ts'
import {ChangeEvent, useRef, useState} from 'react'
import i18n, {useTranslation} from '@/translations/i18n.tsx'
import {ColumnVisibilityMenu} from '@components/commons/columns-visibility-menu/ColumnVisibilityMenu.tsx'
import {TreatmentRequestsColumn} from '@/features/treatment-requests/types.ts'
import {TreatmentRequestsTable} from '@/features/treatment-requests/components/treatment-requests-table/TreatmentRequestsTable.tsx'
import {Flexbox} from '@components/ui/flexbox/FlexBox.tsx'
import {useTreatmentRequests} from '@/features/treatment-requests/services/useTreatmentRequests.ts'
import {SelectedRowsText} from '@components/commons/selected-rows-text/SelectedRowsText.tsx'
import {ChangeStatusMenu} from '@/features/treatment-requests/components/change-status-menu/ChangeStatusMenu.tsx'
import {Download01Icon} from '@components/ui/icon/Icon.tsx'
import {Button} from '@components/ui/button/Button.tsx'
import {httpExportTreatmentRequests} from '@/features/treatment-requests/services/treatment-requests.http.ts'
import {TableFilters} from '@/components/commons/table-filters/TableFilters'
import {SortingMenu} from '@/components/commons/sorting-menu/SortingMenu'
import {
    treatmentRequestColumnToLabel,
    treatmentRequestTableToHiddenColumns,
    useTreatmentRequestSearchParams
} from '@/features/treatment-requests/utils'
import {ViewSelect} from '@/features/views/views-select/ViewSelect'
import {ExportModal} from '@/components/commons/export-modal/ExportModal'

const makeColumnsMap = () =>
    [
        {
            id: TreatmentRequestsColumn.enum.fullName,
            label: i18n.t(treatmentRequestColumnToLabel[TreatmentRequestsColumn.enum.fullName])
        },
        {
            id: TreatmentRequestsColumn.enum.serviceType,
            label: i18n.t(treatmentRequestColumnToLabel[TreatmentRequestsColumn.enum.serviceType])
        },
        {
            id: TreatmentRequestsColumn.enum.sector,
            label: i18n.t(treatmentRequestColumnToLabel[TreatmentRequestsColumn.enum.sector])
        },
        {
            id: TreatmentRequestsColumn.enum.status,
            label: i18n.t(treatmentRequestColumnToLabel[TreatmentRequestsColumn.enum.status])
        },
        {
            id: TreatmentRequestsColumn.enum.createdAt,
            label: i18n.t(treatmentRequestColumnToLabel[TreatmentRequestsColumn.enum.createdAt])
        },
        {
            id: TreatmentRequestsColumn.enum.expirationDate,
            label: i18n.t(treatmentRequestColumnToLabel[TreatmentRequestsColumn.enum.expirationDate])
        },
        {
            id: TreatmentRequestsColumn.enum.diagnosisId,
            label: i18n.t(treatmentRequestColumnToLabel[TreatmentRequestsColumn.enum.diagnosisId])
        },
        {
            id: TreatmentRequestsColumn.enum.type,
            label: i18n.t(treatmentRequestColumnToLabel[TreatmentRequestsColumn.enum.type])
        },
        {
            id: TreatmentRequestsColumn.enum.score,
            label: i18n.t(treatmentRequestColumnToLabel[TreatmentRequestsColumn.enum.score])
        },
        {
            id: TreatmentRequestsColumn.enum.residenceAslDistrictId,
            label: i18n.t(treatmentRequestColumnToLabel[TreatmentRequestsColumn.enum.residenceAslDistrictId])
        }
    ] as const satisfies {id: TreatmentRequestsColumn; label: string}[]

export const TreatmentRequestsAll = () => {
    const {t} = useTranslation()
    const {searchParams, setSearchParams} = useTreatmentRequestSearchParams()
    const columnsMap = makeColumnsMap()
    const inputSearchRef = useRef<HTMLInputElement | null>(null)
    const [isSelectAll, setIsSelectAll] = useState(false)
    const [unselectedTreatmentRequestsIds, setUnselectedTreatmentRequestsIds] = useState<string[]>([])
    const [selectedTreatmentRequestsIds, setSelectedTreatmentRequestsIds] = useState<string[]>([])
    const treatmentRequestsQuery = useTreatmentRequests(
        {
            limit: searchParams.limit,
            page: searchParams.page,
            search: searchParams.search
        },
        {
            filters: filtersParamToPayload(searchParams.filters)?.filters,
            sorts: sortParamToPayload(searchParams.sorts)
        },
        {enabled: false}
    )
    const selectedTreatmentRequestsCount = isSelectAll
        ? treatmentRequestsQuery.persistedPagination.total - unselectedTreatmentRequestsIds.length
        : selectedTreatmentRequestsIds.length
    const onSearch = debounce((event: ChangeEvent<HTMLInputElement>) => {
        setSearchParams({search: event.target.value || undefined, page: 1})
    }, 1000)

    const selectedTreatmentRequestsPatientIds = treatmentRequestsQuery.data?.data
        .filter(dataItem =>
            getIsRowSelected({
                dataItem,
                selectedIds: selectedTreatmentRequestsIds,
                unselectedIds: unselectedTreatmentRequestsIds,
                isSelectAll
            })
        )
        .map(dataItem => dataItem.patientId)

    const resetSelectionStates = () => {
        setIsSelectAll(false)
        setSelectedTreatmentRequestsIds([])
        setUnselectedTreatmentRequestsIds([])
    }

    const columnVisibility =
        columnVisibilitySearchParamsToPayload(TreatmentRequestsColumn, searchParams.columnVisibility) ??
        Object.fromEntries(TreatmentRequestsColumn.options.map(column => [column, true]))

    return (
        <>
            <FiltersBar>
                <Flexbox align="center" gap={2}>
                    <Flexbox align="center" gap={2}>
                        <TableInputSearch
                            defaultValue={searchParams.search}
                            onChange={onSearch}
                            placeholder={t('commons:search_patient')}
                        />
                        {selectedTreatmentRequestsCount >= 1 && (
                            <>
                                <SelectedRowsText count={selectedTreatmentRequestsCount} />
                                <ChangeStatusMenu
                                    patientIds={selectedTreatmentRequestsPatientIds || []}
                                    selectedTreatmentRequestsCount={selectedTreatmentRequestsCount}
                                    unselectedTreatmentRequestsIds={unselectedTreatmentRequestsIds}
                                    selectedTreatmentRequestsIds={selectedTreatmentRequestsIds}
                                    selectedTreatmentRequests={
                                        treatmentRequestsQuery.data?.data.filter(dataItem =>
                                            getIsRowSelected({
                                                dataItem,
                                                selectedIds: selectedTreatmentRequestsIds,
                                                unselectedIds: unselectedTreatmentRequestsIds,
                                                isSelectAll
                                            })
                                        ) ?? []
                                    }
                                />
                                <ExportModal
                                    trigger={
                                        <Button variant="ghost" size="sm">
                                            <Download01Icon />
                                            {t('commons:export')}
                                        </Button>
                                    }
                                    asyncExport
                                    fileName="Richieste di trattamento"
                                    promise={({extension, mode}) =>
                                        httpExportTreatmentRequests({
                                            payload: {
                                                selectedTreatmentRequestIds: selectedTreatmentRequestsIds,
                                                exceptedTreatmentRequestIds: unselectedTreatmentRequestsIds,
                                                type: extension,
                                                filters: filtersParamToPayload(searchParams.filters)?.filters,
                                                fields:
                                                    mode == 'view'
                                                        ? TreatmentRequestsColumn.exclude([
                                                              'select',
                                                              'moreActions'
                                                          ]).options.filter(
                                                              column =>
                                                                  columnVisibility[column] &&
                                                                  !treatmentRequestTableToHiddenColumns.all.some(
                                                                      hiddenColumn => hiddenColumn == column
                                                                  )
                                                          )
                                                        : TreatmentRequestsColumn.exclude([
                                                              'select',
                                                              'moreActions'
                                                          ]).options.filter(
                                                              column =>
                                                                  !treatmentRequestTableToHiddenColumns.all.some(
                                                                      hiddenColumn => hiddenColumn == column
                                                                  )
                                                          ),
                                                sorts: sortParamToPayload(searchParams.sorts)
                                            }
                                        })
                                    }
                                    selectedCount={selectedTreatmentRequestsCount}
                                    allowedExtensions={['xlsx', 'xls', 'csv']}
                                />
                            </>
                        )}
                    </Flexbox>
                </Flexbox>
                {selectedTreatmentRequestsCount == 0 && (
                    <ViewSelect entity="treatmentRequestsAll" searchInputRef={inputSearchRef} />
                )}
                {selectedTreatmentRequestsCount == 0 && (
                    <Flexbox style={{flexShrink: 0}} align="center" gap={2}>
                        <TableFilters
                            onApply={filters => {
                                setSearchParams({filters: JSON.stringify(filters), page: 1})
                                resetSelectionStates()
                            }}
                            filtersParams={searchParams.filters}
                            entity="treatmentRequests"
                            availableFilters={[
                                {
                                    key: TreatmentRequestsColumn.enum.serviceType,
                                    label: treatmentRequestColumnToLabel[TreatmentRequestsColumn.enum.serviceType]
                                },
                                {
                                    key: TreatmentRequestsColumn.enum.sector,
                                    label: treatmentRequestColumnToLabel[TreatmentRequestsColumn.enum.sector]
                                },
                                {
                                    key: TreatmentRequestsColumn.enum.status,
                                    label: treatmentRequestColumnToLabel[TreatmentRequestsColumn.enum.status]
                                },
                                {
                                    key: TreatmentRequestsColumn.enum.createdAt,
                                    label: treatmentRequestColumnToLabel[TreatmentRequestsColumn.enum.createdAt]
                                },
                                {
                                    key: TreatmentRequestsColumn.enum.expirationDate,
                                    label: treatmentRequestColumnToLabel[TreatmentRequestsColumn.enum.expirationDate]
                                },
                                {
                                    key: TreatmentRequestsColumn.enum.type,
                                    label: treatmentRequestColumnToLabel[TreatmentRequestsColumn.enum.type]
                                },
                                {
                                    key: TreatmentRequestsColumn.enum.score,
                                    label: treatmentRequestColumnToLabel[TreatmentRequestsColumn.enum.score]
                                },
                                {
                                    key: TreatmentRequestsColumn.enum.diagnosisId,
                                    label: treatmentRequestColumnToLabel[TreatmentRequestsColumn.enum.diagnosisId]
                                },
                                {
                                    key: TreatmentRequestsColumn.enum.residenceAslDistrictId,
                                    label: treatmentRequestColumnToLabel[
                                        TreatmentRequestsColumn.enum.residenceAslDistrictId
                                    ]
                                }
                            ]}
                        />
                        <SortingMenu
                            columns={columnsMap}
                            order={sortParamToPayload(searchParams.sorts) ?? []}
                            setOrder={sorts => {
                                setSearchParams({sorts: JSON.stringify(sorts)})
                                resetSelectionStates()
                            }}
                        />
                        <ColumnVisibilityMenu
                            columns={columnsMap}
                            columnVisibility={{
                                ...columnVisibility,
                                ...Object.fromEntries(
                                    treatmentRequestTableToHiddenColumns.all.map(column => [column, false])
                                )
                            }}
                            onSubmit={columnVisibility =>
                                setSearchParams({columnVisibility: columnVisibilityToSearchParams(columnVisibility)})
                            }
                        />
                    </Flexbox>
                )}
            </FiltersBar>
            <TreatmentRequestsTable
                columnVisibility={columnVisibility}
                isSelectAll={isSelectAll}
                setIsSelectAll={setIsSelectAll}
                selectedTreatmentRequestsCount={selectedTreatmentRequestsCount}
                selectedTreatmentRequestsIds={selectedTreatmentRequestsIds}
                setSelectedTreatmentRequestsIds={setSelectedTreatmentRequestsIds}
                unselectedTreatmentRequestsIds={unselectedTreatmentRequestsIds}
                setUnselectedTreatmentRequestsIds={setUnselectedTreatmentRequestsIds}
            />
        </>
    )
}
